import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import TemplatePreview, { PreviewData } from './TemplatePreview';
import { BuonoDetail, Template } from '../../services/api';
import './GeneraBuono.css';

const resolveImageUrl = (value?: string | null) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:') || value.startsWith('/')) {
    return value;
  }
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  return `${apiBase}/storage/${value}`;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/** Converte l'URL immagine in data URI così html2canvas la disegna (niente CORS). */
function fetchImageAsDataUri(
  url: string | null | undefined,
  options?: RequestInit
): Promise<string | null> {
  if (!url || url.startsWith('data:')) return Promise.resolve(url || null);
  const headers: HeadersInit = {
    ...(options?.headers as Record<string, string>),
  };
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { mode: 'cors', credentials: 'omit', ...options, headers })
    .then((r) => (r.ok ? r.blob() : Promise.reject(new Error('Fetch failed'))))
    .then(
      (blob) =>
        new Promise<string | null>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        })
    )
    .catch(() => null);
}

/** Attende che tutte le img dentro l'elemento siano caricate (o timeout). */
function waitForImages(el: HTMLElement, timeoutMs: number): Promise<void> {
  const imgs = Array.from(el.querySelectorAll<HTMLImageElement>('img'));
  if (imgs.length === 0) return Promise.resolve();
  return Promise.race([
    Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    ).then(() => {}),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

/**
 * Cattura = stessa dimensione della preview (100% visibile). PDF = postcard 107×75.9mm.
 */
const WIDTH_PX = 404.36;
const HEIGHT_PX = 602; // come in preview, così si vede tutto (non 573.7 che tagliava ~60%)
const HALF_HEIGHT_PX = HEIGHT_PX / 2; // 301

/** PDF: una pagina verticale 107mm × 151.8mm (fronte sopra, retro sotto) per stampare e piegare a metà */
const POSTCARD_WIDTH_MM = 107;
const POSTCARD_HEIGHT_MM = 75.9;
const PDF_PAGE_WIDTH_MM = POSTCARD_WIDTH_MM;
const PDF_PAGE_HEIGHT_MM = POSTCARD_HEIGHT_MM * 2;

/** Carica un'immagine da data URI. */
function loadImage(dataUri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUri;
  });
}

/**
 * Sostituisce tutti gli SVG inline con <img> a risoluzione alta (scale×), così in PDF
 * non risultano pixelati. Nel clone l'img ha dimensioni di visualizzazione originali
 * ma il src è un SVG con width/height scalati, così la rasterizzazione è nitida.
 */
function replaceInlineSvgsWithImages(container: HTMLElement, scale: number): void {
  const svgs = Array.from(container.querySelectorAll('svg'));
  svgs.forEach((svg) => {
    const wAttr = svg.getAttribute('width') || '';
    const hAttr = svg.getAttribute('height') || '';
    const wNum = parseFloat(wAttr) || svg.getBoundingClientRect().width || 24;
    const hNum = parseFloat(hAttr) || svg.getBoundingClientRect().height || 24;
    let svgStr = new XMLSerializer().serializeToString(svg);
    const wScaled = Math.round(wNum * scale);
    const hScaled = Math.round(hNum * scale);
    svgStr = svgStr.replace(/\swidth="[^"]*"/, ` width="${wScaled}"`);
    svgStr = svgStr.replace(/\sheight="[^"]*"/, ` height="${hScaled}"`);
    if (!/viewBox\s*=/i.test(svgStr)) {
      svgStr = svgStr.replace(/<svg/, `<svg viewBox="0 0 ${wNum} ${hNum}"`);
    }
    if (!/^\s*<svg[^>]*\swidth\s*=/i.test(svgStr)) {
      svgStr = svgStr.replace(/<svg/, `<svg width="${wScaled}" height="${hScaled}"`);
    }
    const dataUri = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgStr)));
    const img = container.ownerDocument!.createElement('img');
    img.setAttribute('src', dataUri);
    img.setAttribute('width', String(wNum));
    img.setAttribute('height', String(hNum));
    img.style.display = 'block';
    img.style.width = /^\d+$/.test(String(wNum)) ? wNum + 'px' : String(wNum);
    img.style.height = /^\d+$/.test(String(hNum)) ? hNum + 'px' : String(hNum);
    svg.parentNode?.replaceChild(img, svg);
  });
}

/**
 * Disegna il retro (bottom) su un canvas: sfondo + logo centrato (object-fit contain).
 * L'img del retro non compare nello screenshot html2canvas, quindi la disegniamo a mano.
 */
function drawBottomToCanvas(
  widthPx: number,
  heightPx: number,
  scale: number,
  backgroundColor: string,
  logoDataUri: string | null | undefined
): Promise<string> {
  const w = Math.round(widthPx * scale);
  const h = Math.round(heightPx * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(canvas.toDataURL('image/jpeg', 1));

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, w, h);

  if (!logoDataUri) {
    return Promise.resolve(canvas.toDataURL('image/jpeg', 1));
  }

  return loadImage(logoDataUri)
    .then((img) => {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      if (iw <= 0 || ih <= 0) return canvas.toDataURL('image/jpeg', 1);
      const scaleImg = Math.min(w / iw, h / ih);
      const sw = iw * scaleImg;
      const sh = ih * scaleImg;
      const x = (w - sw) / 2;
      const y = (h - sh) / 2;
      ctx.drawImage(img, x, y, sw, sh);
      return canvas.toDataURL('image/jpeg', 1);
    })
    .catch(() => canvas.toDataURL('image/jpeg', 1));
}

interface BuonoPdfExportProps {
  buono: BuonoDetail;
  filename: string;
  onDone: () => void;
  /** Se fornito, riceve il blob del PDF (es. per upload e link nel messaggio) invece di avviare il download. */
  onDoneWithBlob?: (blob: Blob) => void;
  /** Opzionale: permette di usare una scala diversa per il PDF (es. più bassa per upload SMTP). */
  scaleOverrideForPdf?: number;
}

/**
 * Un solo screenshot della card (come in preview), poi ritaglio metà superiore e inferiore.
 * PDF: pagina 1 = retro (bottom), pagina 2 = fronte (top). Stesse proporzioni della preview.
 */
const BuonoPdfExport: React.FC<BuonoPdfExportProps> = ({ buono, filename, onDone, onDoneWithBlob, scaleOverrideForPdf }) => {
  const cardPreviewRef = useRef<HTMLDivElement>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const onDoneWithBlobRef = useRef(onDoneWithBlob);
  onDoneWithBlobRef.current = onDoneWithBlob;
  const template = buono.template;
  const isT1 = template?.slug === 'template_1';
  /** Immagine retro come data URI per il PDF (evita CORS in html2canvas) */
  const [logoDataUri, setLogoDataUri] = useState<string | null | undefined>(undefined);
  const [backLogoDataUri, setBackLogoDataUri] = useState<string | null | undefined>(undefined);
  /** Template 2: background fronte (bg_data_image) come data URI per html2canvas */
  const [frontImageDataUri, setFrontImageDataUri] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!template) return;
    if (isT1) {
      const path = template.image_logo;
      const useAsset = path && !/^https?:\/\//i.test(path) && !path.startsWith('data:');
      const url = useAsset
        ? `${API_URL}/admin/templates/asset?path=${encodeURIComponent(path)}`
        : (template.image_logo_url || (template.image_logo ? resolveImageUrl(template.image_logo) : null));
      if (url) {
        fetchImageAsDataUri(url).then(setLogoDataUri);
      } else {
        setLogoDataUri(null);
      }
    } else {
      const pathBack = template.bg_logo_image;
      const useAssetBack = pathBack && !/^https?:\/\//i.test(pathBack) && !pathBack.startsWith('data:');
      const urlBack = useAssetBack
        ? `${API_URL}/admin/templates/asset?path=${encodeURIComponent(pathBack)}`
        : (template.bg_logo_image_url || (template.bg_logo_image ? resolveImageUrl(template.bg_logo_image) : null));
      if (urlBack) {
        fetchImageAsDataUri(urlBack).then(setBackLogoDataUri);
      } else {
        setBackLogoDataUri(null);
      }
      const pathFront = template.bg_data_image;
      const useAssetFront = pathFront && !/^https?:\/\//i.test(pathFront) && !pathFront.startsWith('data:');
      const urlFront = useAssetFront
        ? `${API_URL}/admin/templates/asset?path=${encodeURIComponent(pathFront)}`
        : (template.bg_data_image_url || (template.bg_data_image ? resolveImageUrl(template.bg_data_image) : null));
      if (urlFront) {
        fetchImageAsDataUri(urlFront).then(setFrontImageDataUri);
      } else {
        setFrontImageDataUri(null);
      }
    }
  }, [template, isT1]);

  useEffect(() => {
    if (!template) {
      onDoneRef.current();
    }
  }, [template]);

  const readyForExport =
    template &&
    (isT1 ? logoDataUri !== undefined : backLogoDataUri !== undefined && frontImageDataUri !== undefined);

  useEffect(() => {
    if (!readyForExport) return;
    const el = cardPreviewRef.current;
    if (!el) {
      onDoneRef.current();
      return;
    }
    const run = async () => {
      await waitForImages(el, 2000);
      await new Promise((r) => setTimeout(r, isT1 ? 200 : 500));
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));

      const scale = typeof scaleOverrideForPdf === 'number' && scaleOverrideForPdf > 0 ? scaleOverrideForPdf : 8;
      const cardEl = el.firstElementChild as HTMLElement | null;
      if (!cardEl || cardEl.children.length < 2) {
        onDoneRef.current();
        return;
      }

      try {
        // Sostituiamo gli SVG con img (data URI) nel clone così html2canvas non li rompe
        const fullCardCanvas = await html2canvas(el, {
          scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          width: WIDTH_PX,
          height: HEIGHT_PX,
          windowWidth: WIDTH_PX,
          windowHeight: HEIGHT_PX,
          onclone: (_doc, clonedEl) => {
            replaceInlineSvgsWithImages(clonedEl, scale);
          },
        });
        const w = fullCardCanvas.width;
        const h = fullCardCanvas.height;
        const halfH = h / 2;

        // Ritaglio metà superiore e metà inferiore (preview: prima retro, poi fronte)
        const topCanvas = document.createElement('canvas');
        topCanvas.width = w;
        topCanvas.height = halfH;
        const topCtx = topCanvas.getContext('2d');
        if (topCtx) {
          topCtx.drawImage(fullCardCanvas, 0, 0, w, halfH, 0, 0, w, halfH);
        }
        const bottomCanvas = document.createElement('canvas');
        bottomCanvas.width = w;
        bottomCanvas.height = halfH;
        const bottomCtx = bottomCanvas.getContext('2d');
        if (bottomCtx) {
          bottomCtx.drawImage(fullCardCanvas, 0, halfH, w, halfH, 0, 0, w, halfH);
        }
        // Se il logo non compare nello screenshot (html2canvas), sovrapponilo sul ritaglio retro (metà superiore nella preview)
        const logoUri = isT1 ? logoDataUri ?? null : backLogoDataUri ?? null;
        if (logoUri && topCtx) {
          try {
            const img = await loadImage(logoUri);
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              const scaleImg = Math.min(w / img.naturalWidth, halfH / img.naturalHeight);
              const sw = img.naturalWidth * scaleImg;
              const sh = img.naturalHeight * scaleImg;
              topCtx.drawImage(img, (w - sw) / 2, (halfH - sh) / 2, sw, sh);
            }
          } catch {
            // ignora se il logo non carica
          }
        }

        // Riga bianca in corrispondenza della piega (solo nel PDF)
        const foldLinePx = Math.max(4, Math.round(h / 150)); // ~4–8 px a seconda della risoluzione
        const topH = halfH - foldLinePx / 2;
        const bottomY = halfH + foldLinePx / 2;
        const bottomH = halfH - foldLinePx / 2;

        const onePageCanvas = document.createElement('canvas');
        onePageCanvas.width = w;
        onePageCanvas.height = h;
        const oneCtx = onePageCanvas.getContext('2d');
        if (oneCtx) {
          oneCtx.drawImage(topCanvas, 0, 0, w, halfH, 0, 0, w, topH);
          oneCtx.fillStyle = '#ffffff';
          oneCtx.fillRect(0, topH, w, foldLinePx);
          oneCtx.drawImage(bottomCanvas, 0, 0, w, halfH, 0, bottomY, w, bottomH);
        }
        const onePageDataUrl = onePageCanvas.toDataURL('image/jpeg', 0.9);

        const pdf = new jsPDF('p', 'mm', [PDF_PAGE_WIDTH_MM, PDF_PAGE_HEIGHT_MM]);
        const pageSize = (pdf as any).internal?.pageSize;
        const pageW = pageSize?.getWidth?.() ?? PDF_PAGE_WIDTH_MM;
        const pageH = pageSize?.getHeight?.() ?? PDF_PAGE_HEIGHT_MM;
        pdf.addImage(onePageDataUrl, 'JPEG', 0, 0, pageW, pageH);
        const blob = pdf.output('blob');
        if (onDoneWithBlobRef.current) {
          onDoneWithBlobRef.current(blob);
        } else {
          pdf.save(filename);
        }
      } finally {
        onDoneRef.current();
      }
    };
    run();
  }, [readyForExport, filename]);

  if (!template) return null;

  const previewData: PreviewData = {
    regalo: buono.regalo ?? '',
    per: buono.a ?? '',
    da: buono.da ?? '',
    validoFino: buono.scade_at || undefined,
  };

  return (
    <>
      {/* Sotto: preview a dimensione reale (necessaria per html2canvas). Non nascosta, altrimenti il PDF si rompe. */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.95)',
        }}
      >
        <div
          className="genera-crea-anteprima genera-pdf-export"
          style={{
            width: WIDTH_PX,
            minWidth: WIDTH_PX,
            height: HEIGHT_PX,
            minHeight: HEIGHT_PX,
            background: '#fdf2f8',
          }}
        >
          <div
            ref={cardPreviewRef}
            style={{
              width: WIDTH_PX,
              height: HEIGHT_PX,
              minHeight: HEIGHT_PX,
              maxHeight: HEIGHT_PX,
              overflow: 'hidden',
              background: '#fdf2f8',
            }}
          >
            <TemplatePreview
              template={template as Template}
              resolveImageUrl={resolveImageUrl}
              previewData={previewData}
              imageLogoDataUri={isT1 ? logoDataUri : undefined}
              backLogoDataUri={!isT1 ? backLogoDataUri : undefined}
              frontImageDataUri={!isT1 ? frontImageDataUri : undefined}
            />
          </div>
        </div>
      </div>

      {/* Sopra: popup "Generazione buono..." (stesso stile degli altri modali), così l'utente non vede la card */}
      <div
        className="genera-share-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="genera-pdf-modal-title"
        style={{ zIndex: 10001 }}
      >
        <div className="genera-share-modal">
          <div className="genera-share-modal-header">
            <h3 id="genera-pdf-modal-title" className="genera-share-modal-title">
              Generazione buono...
            </h3>
          </div>
          <p className="genera-pdf-modal-subtitle">Stiamo preparando il PDF, attendi qualche secondo.</p>
        </div>
      </div>
    </>
  );
};

export default BuonoPdfExport;
